import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { FieldRequirement, mergeDescriptionIds, premiumFieldPresentation, resolveFieldPresentation } from "../premium/fieldRequirements";
import { premiumTheme } from "../premium/premiumTheme";
import { PremiumDropdownSurface, premiumFormFieldStyles } from "../premium/premiumFormStyles";
import { Tooltip } from "antd";
import styled from "styled-components";
import TagComponent from "./TagComponent";
import { v4 as uuidv4 } from "uuid";
import DropdownOverlay, { OverlayType } from "../dropdownOverlay/PremiumDropdownOverlay";
import { getDropdownData } from "../../../utils/dropdownData";
import Mail05Icon from "../../../assets/icons/mail-05.svg";
import * as Library from "../../../utils/Library";
import { maskEmail, maskPhoneNumber, maskName } from "../../../utils/valuesMasking";
import Edit03 from "../../../assets/icons/edit-03.svg";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import CheckboxVector from "../checkbox/CheckboxVector";

// #region SCOPED CSS
const StyledElems = styled.div`
    & {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        align-self: stretch;
    }

    textarea.autoResize {
        min-height: ${props => props.formFieldHeight ?? "37px"};
        background: transparent;
        border: 0;
        width: 100%;
        outline: none;
        //add resize button
        //resize: vertical !important;
    }

    .inputFieldContainer {
        padding: 0px;
    }

    .FormComponentContainer {
        width: 100%;
        height: 100%;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 4px;
        display: inline-flex;
        position: relative;
    }

    .Frame51 {
        align-self: stretch;
        padding-left: 5px;
        padding-right: 5px;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;
        display: inline-flex;
    }

    .Icon {
        width: 11px;
        height: 11px;
        color: var(--text-icons-gray-200);
        position: relative;
        top: -1px;
    }

    .Icon.locked,
    .Icon.mandatoryLocked {
        color: var(--text-icons-gray-300);
    }

    .Icon.disabledSimple {
        color: var(--text-icons-gray-200);
    }

    .Title {
        color: var(--text-icons-gray-200);
        font-size: 0.75rem;
        font-weight: 400;
        word-wrap: break-word;
    }

    .InputSelectorInverted {
        align-self: stretch;
        justify-content: flex-start;
        align-items: flex-start;
        display: flex;
    }

    .Frame49 {
        width: 5px;
        height: ${props => props.formFieldHeight ?? "37px"};
        padding-top: 6px;
        padding-bottom: 6px;
        background: var(--text-icons-orange-500);
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }

    .Search {
        flex: 1 1 0;
        height: 38px;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: flex;
    }

    .Frame48 {
        width: 100%;
        flex: 1 1 0;
        height: ${props => props.formFieldHeight ?? "37px"};
        background: #10141b;
        border-radius: 4px;
        background: var(--backgrounds-lines-n-600);
        border: 0;
        border-left: 5px solid var(--backgrounds-lines-n-500);
        outline: none;
        color: var(--text-icons-gray-100);
        gap: 0;
    }

    .Frame48.none {
        border: 0 !important;
        gap: 0;
    }

    .Frame48.none .Frame48Input {
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;
    }

    .Frame48.simple {
        border-left: 5px solid var(--backgrounds-lines-n-500);
    }

    .Frame48.disabled {
        color: var(--text-icons-gray-300);
        cursor: not-allowed;
    }

    .Frame48.mandatory {
        border-left: 5px solid var(--text-icons-orange-500);
    }

    .Frame48.recommended {
        border-left: 5px solid var(--text-icons-blue-500);
    }

    .Frame48.locked,
    .Frame48.mandatoryLocked {
        border-left: 5px solid var(--text-icons-gray-300);
        cursor: not-allowed !important;
    }

    .Frame48.disabledSimple {
        border-left: 5px solid var(--backgrounds-lines-n-500, #141922);
    }

    .Frame48.invalid {
        border-left: 5px solid var(--text-icons-red-500);
    }

    .Frame48.tagAsDropdown {
        cursor: pointer;
    }

    .dragged {
        box-shadow: 0px 0px 0px 1px var(--text-icons-blue-500);
        border-radius: 4px;
    }

    .dragged.drag-over {
        box-shadow: none;
    }

    .dropped {
        box-shadow: 0px 0px 0px 1px var(--text-icons-green-500);
        border-radius: 4px;
    }

    .droppedRemove,
    .dropdownArrow {
        position: absolute;
        right: 12px;
        top: 10px; //->TODO: CHECK THIS (CONFLICTING)
        width: 16px;
        height: 16px;
        cursor: pointer;
        //color: #d1d1d1;
    }

    .dropdownArrow {
        transition: transform 0.2s;
    }

    .dropdownArrow.active {
        transform: rotate(180deg);
    }

    .dropdownArrow.disabled {
        cursor: not-allowed !important;
        color: var(--text-icons-gray-300) !important;
    }

    .Frame48.tagAsDropdown .dropdownArrow {
        position: unset !important;
        align-self: center;
        margin-right: 12px;
        height: 17px;
        width: 16px;
        flex-shrink: 0;
    }

    .Frame48.tagAsDropdown .Frame48TagsContainer {
        padding-right: 42px;
    }

    .Frame48LeftContent {
        display: flex;
        width: 50px;
        height: ${props => props.formFieldHeight ?? "37px"};
        padding: 6px 0px;
        justify-content: center;
        align-items: center;
        gap: 8px;

        border-right: 1px solid var(--backgrounds-lines-n-100);
        flex-shrink: 0;
        overflow: hidden;
    }

    .Frame48LeftContent.useFieldStateColor {
        color: var(--left-content-field-color);
    }

    .Frame48RightContent {
        display: flex;
        width: 50px;
        height: ${props => props.formFieldHeight ?? "37px"};
        padding: 6px 0px;
        justify-content: center;
        align-items: center;
        gap: 8px;

        border-left: 1px solid var(--backgrounds-lines-n-100);
        flex-shrink: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0;
    }

    .Frame48Input {
        flex: 1 1 0;
        width: 100%;
        height: 100%;

        //padding-left: 12px;
        //padding-right: 12px;
        padding: 0px;

        background: var(--backgrounds-lines-n-600);
        border: 0;
        outline: none;
        color: var(--text-icons-gray-100);

        /* Subtitle 2 */
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    textarea.Frame48Input {
        resize: none;
        padding-top: 10px;
        padding-bottom: 6px;
    }

    textarea.Frame48Input:not(.autoResize) {
        height: auto !important;
    }

    .inputFieldContainer {
        display: flex;
        flex: 1 1 0;
        height: 100%;
        flex-direction: row;
        align-items: center;
        padding-left: 12px;
        padding-right: 12px;
    }

    .textPrefix {
        color: var(--text-icons-gray-300, #4f5b6d);
        font-size: 14px;
        font-style: italic;
        font-weight: 400;
        line-height: normal;
    }

    //WHEN NUMBER REMOVE THE UP AND DOWN ARROWS
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    //WHEN NUMBER REMOVE THE UP AND DOWN ARROWS (FIREFOX)
    input[type="number"] {
        -moz-appearance: textfield;
        appearance: textfield;
    }

    input[type="date"]::-webkit-calendar-picker-indicator,
    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
        display: none;
    }

    .Frame48Input::placeholder,
    .TagsInput::placeholder,
    label.placeholder {
        color: var(--text-icons-gray-200) !important;
        font-style: italic;
    }

    .Frame48.disabled .Frame48Input::placeholder,
    .Frame48.disabled .TagsInput::placeholder,
    .Frame48.disabledSimple .Frame48Input::placeholder,
    .Frame48.disabledSimple .TagsInput::placeholder,
    .Frame48.locked .Frame48Input::placeholder,
    .Frame48.locked .TagsInput::placeholder,
    .Frame48.mandatoryLocked .Frame48Input::placeholder,
    .Frame48.mandatoryLocked .TagsInput::placeholder {
        color: var(--text-icons-gray-300) !important;
    }

    .Frame48TagsContainer {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 6px 0px 6px 8px;
        width: 100%;
        align-self: center;
    }

    .TagsInput {
        color: #d1d1d1;
        background: transparent;
        border: 0;
        outline: none;

        /* Subtitle 2 */
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .Frame48.disabled .Frame48Input {
        color: var(--text-icons-gray-300);
        cursor: not-allowed;
    }

    .Frame50 {
        align-self: stretch;
        padding-left: 5px;
        padding-right: 5px;
        text-align: right;
    }

    .Frame50.withCheckbox {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 4px;
    }

    .AssistiveTextCheckbox {
        display: flex;
        align-items: center;
        padding: 0;
        border: 0;
        background: none;
    }

    .AssistiveText {
        /* Label/XS - Uppercase */
        font-size: 9px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.1px;
        text-transform: uppercase;
        height: 9px;
    }

    input.datepicker {
        display: inline-block;
        position: relative;
        color-scheme: dark;
    }

    input[type="date"]::-webkit-calendar-picker-indicator {
        background: transparent;
        bottom: 0;
        color: transparent;
        cursor: pointer;
        height: auto;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        width: auto;
        color-scheme: dark;
    }

    div.no-date {
        background: none;
    }

    div.no-date .placeholder {
        display: block !important;
        position: absolute;
        top: 0;
        left: 0;
        background: var(--backgrounds-lines-n-600);
        height: ${props => props.formFieldHeight ?? "37px"};
        width: 100%;
        line-height: ${props => props.formFieldHeight ?? "37px"};
        padding-left: 12px;
        padding-right: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    div.has-date .placeholder {
        display: none;
    }

    .ip-placeholder {
        color: var(--text-icons-gray-200, #7c868b);
        font-family: Roboto;
        font-size: 12px;
        font-style: italic;
        font-weight: 400;
        line-height: normal;
    }

    .field-edit-option {
        cursor: pointer;
    }

    .field-edit-option img {
        width: 10px;
        margin-left: 4px;
        align-items: center;
        justify-content: center;
        display: flex;
    }
    ${premiumFieldPresentation}
    ${premiumFormFieldStyles}
`;
// #endregion

/** STYLING PROPS (SO FAR)
 * @param {Object} styling - The styling object for the component.
 * @param {string} styling.backgroundColor - The background color for the component. // TODO: Currently only works with dropdowns, need to implement for other types
 * @param {string} [styling.dropdownOverlayTop="65px"] - The top position for the dropdown overlay.
 * @param {string} [styling.dropdownOverlayWidth] - The width for the dropdown overlay.
 * @param {Object} [styling.dropdownOverlayStyle={}] - The style for the dropdown overlay.
 * @param {string} [styling.requiredTypeColor] - The color override for the required type indicator.
 * @param {string} [styling.inputStyles] - The style override for the input field.
 * @param {string} [styling.tagStyles] - The style override for the tags.
 * @param {string} [styling.textColorOverride] - The color override for the text.
 * @param {string} [styling.formFieldWidth] - The width override for the input wrapper.
 * @param {string} [styling.formFieldMaxWidth] - The max width override for the input wrapper.
 * @param {string} [styling.formFieldMinWidth] - The min width override for the input wrapper.
 * @param {string} [styling.formFieldHeight="37px"] - The height override for the input wrapper.
 */

/** A simple form component that renders an input field with multiple customizations.
 * for examples check route: /templates/forms and FormsTemplatePage.jsx
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the input field.
 * @param {string} props.varName - The variable name of the input field.
 * @param {string} props.type - The type of the input field. Possible values are "text", "number", "email", "password", "date", "datetime-local", "dropdown", "tags", "droppable", "taxNumber", "registrationNumber" and "phoneNumber"
 * @param {boolean} [props.editable=false] - The flag to know if the input field is editable.
 * @param {"legacy"|"premium"} [props.appearance="legacy"] - Premium presentation is opt-in; legacy validation remains authoritative.
 * @param {"suggested"|"actual"} [props.valuePresentation="actual"] Premium populated-value styling only.
 * @param {"mandatory"|"recommended"|"optional"} [props.requirement] - Independent premium metadata; never adds validation.
 * @param {"enabled"|"locked"|"disabled"} [props.interactionState] - Premium restriction; cannot lift a legacy lock.
 * @param {string} [props.requiredType="simple"] - The type of required field indicator. Possible values are "simple", "mandatory", "recommended", "locked", "mandatoryLocked", "disabledSimple" or "none".
 * @param {string} props.value - The value of the input field. NOTE: If this is an array, this should come from a state variable or else it will trigger a useEffect of data was changed on every view refresh.
 * @param {string} props.valueCover - This will hide the value of the input field and show the valueCover instead.
 * @param {boolean} [props.useValueMasking=false] - This will mask the value of the input field.
 * @param {boolean} [props.allowAutoComplete=true] - This will allow the browser to remember the value of the input field.
 * @param {function} props.onTextChange - The callback function to handle text changes in the input field.
 * @param {function} props.onTextBlur - The callback function to handle text blur in the input field.
 * @param {function} props.onKeyDown - The callback function to handle key down events in the input field.
 * @param {function} props.onDropdownChange - The callback function to handle dropdown changes.
 * @param {string} props.tooltip - The tooltip text for the input field.
 * @param {string} [props.tooltipMaxWidth="200px"] - The maximum width of the tooltip.
 * @param {boolean} [props.disableTooltip=false] - The flag to know if the tooltip should be
 * @param {string} props.hoverTooltip - The tooltip text to show when hovering over the input field.
 * @param {ReactNode} [props.leftContent] - The icon to display on the left side of the input field.
 * @param {boolean} [props.leftContentUseFieldStateColor=false] - Apply the field value color to custom left content that uses currentColor.
 * @param {string} [props.rightContent] - The text to display on the right side of the input field.
 * @param {string} [props.tagAddedColor="#006D00"] - The color of the tag added to the input field.
 * @param {string} [props.tagAddWhenEnterPressed=true] - The flag to know if the tag should be added when the enter key is pressed.
 * @param {function} [props.onTagsChanged] - The callback function to handle tag changes in the input field.
 * @param {function} [props.onTagRemoved] - The callback function to handle tag removal in the input field.
 * @param {boolean} [props.disableTagRemove=false] - The flag to know if the tag remove button should be disabled.
 * @param {string} [props.className=""] - The class name for the component.
 * @param {string} [props.minDate=""] - The minimum date for the date input field.
 * @param {string} [props.maxDate=""] - The maximum date for the date input field.
 * @param {string} [props.assistiveText=""] - The bottom right assistive text for the input field.
 * @param {string} [props.assistiveTextColorOverride=""] - The color of the assistive text.
 * @param {Object} [props.assistiveTextCheckbox] - Optional checkbox beside the standard assistive text.
 * @param {boolean} [props.assistiveTextCheckbox.isChecked=false] - Whether the checkbox is checked.
 * @param {boolean} [props.assistiveTextCheckbox.disabled=false] - Whether the checkbox is disabled.
 * @param {function} [props.assistiveTextCheckbox.onCheckboxClick] - Called when an enabled checkbox is clicked.
 * @param {string} [props.assistiveTextCheckbox.ariaLabel] - Accessible checkbox label.
 * @param {string} [props.invalidAssistiveText="Invalid Text"] - The assistive text to display when the input field is invalid.
 * @param {boolean} [props.disableAssistiveText=false] - The flag to know if the assistive text
 * @param {boolean} [props.assistiveTextSpacing=true] - The flag to know if the assistive text should have spacing.
 * @param {int} [props.numOfLines=1] - The number of lines for the input field. Only applicable for type "text".
 * @param {int} [props.minLength=0] - The minimum length of the input field. Only applicable for type "text".
 * @param {int} [props.maxLength=500] - The maximum length of the input field. Only applicable for type "text".
 * @param {boolean} [props.lengthIgnoreSpaces=false] - The flag to know if the length should ignore spaces. Only applicable for type "text".
 * @param {boolean} [props.isDropdownMultiselect=false] - Whether the dropdown is multi-select. Only applicable for type "dropdown".
 * @param {boolean} [props.isDropdownSearchable=false] - Whether the dropdown is searchable. Only applicable for type "dropdown".
 * @param {Object[]} [props.dropdownData=[]] - The data for the dropdown, only applicable for type "dropdown". values should be composed with fields: value, name, selected(optional) of The selected will be the item that have "selected: true". IMPORTANT: dropdownData should always come from a state variable or else it will trigger a useEffect of data was changed on every view refresh and cause the selection to be reset.
 * @param {function} props.dropdownOnSingleItemClick - The callback function to handle single item click in the dropdown. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {boolean} [props.firstElementIsSelectAll=false] - The flag to know if the first element of the dropdown should be a select all option. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {Object} [props.multidropdownOptions] - The options for the multi dropdown. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {Object} [props.dropdownSettings.pinnedOption] - Optional single-select option displayed above the searchable list.
 * @param {boolean} [props.multidropdownOptions.useSelectAll=false] - The flag to know if the select all option should be used. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {function} [props.multidropdownOptions.placeholderTextFunction] - The custom function to get the name of the selected items. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {string} [props.multidropdownOptions.selectAllText="Select All"] - The text for the select all option. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {string} [props.multidropdownOptions.selectAllDescription] - The description for the select all option. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {boolean} [props.multidropdownOptions.useDoneButton=false] - The flag to know if the done button should be used. Only applicable for type "dropdown" and with isDropdownMultiselect set to true.
 * @param {function} [props.multidropdownOptions.onDoneClick] - Called with selected items after DONE closes a multi dropdown.
 * @param {boolean} [props.dropdownSettings.hasDescriptionFields=false] - The flag to know if the dropdown has description fields. Only applicable for type "dropdown".
 * @param {boolean} [props.dropdownSettings.hasLockedFields=false] - The flag to know if the dropdown has locked fields. Only applicable for type "dropdown".
 * @param {number} [props.dropdownSettings.widthPercentage=undefined] - The width percentage of the dropdown. Only applicable for type "dropdown".
 * @param {boolean} [props.dropdownSettings.hasMiddleFields=false] - The flag to know if the dropdown has middle fields. Only applicable for type "dropdown".
 * @param {string} [props.dropdownSettings.middleFieldKeyName="middleField"] - The key name of the middle field. Only applicable for type "dropdown".
 * @param {Object} [props.dropdownSettings.mainFieldStyle={}] - The style for the main field. Only applicable for type "dropdown".
 * @param {number} [props.dropdownSettings.visibleOptionCount] - Optional visible option-row limit; premium defaults to seven, legacy keeps its pixel limit.
 * @param {Object} [props.dropdownSettings.middleFieldStyle={}] - The style for the middle field. Only applicable for type "dropdown".
 * @param {Object} [props.dropdownSettings.descriptionStyle={}] - The style for the description field. Only applicable for type "dropdown".
 * @param {function} [props.useDropdownPlaceholderAsTag=false] - The flag to know if the placeholder should be used as a tag/chip. Only applicable for type "dropdown".
 * @param {boolean} [props.completeOnOutsideClick=false] - Complete an eligible multi-dropdown via its existing DONE callback on an outside click only.
 * @param {boolean} [props.dismissOnOutsideSvgClick=false] - Allow outside SVG clicks to dismiss this dropdown; retains legacy SVG exemption by default.
 * @param {boolean} [props.useDropdownPortal=false] - When true, the dropdown overlay is rendered via portal to document.body, bypassing any parent overflow:hidden. The trigger stays in place. Only applicable for type "dropdown".
 * @param {string} [props.phoneNumberCountryCode=""] - The country code NUMBER for the phone number input field, example "47". Only applicable for type "phoneNumber".
 * @param {string} [props.phoneNumberCountryCodeName=""] - The country code NAME for the phone number input field, example "NO". Only applicable for type "phoneNumber".
 * @param {string} [props.phoneNumberSpacedEveryCount=0] - Adds a space every X characters for the phone number input field. Only applicable for type "phoneNumber".
 * @param {string} [props.taxNumberCountryCode=""] - The country code for the tax number input field. Only applicable for type "taxNumber".
 * @param {string} [props.onCountryCodeChange] - This is a function triggers when the country code is changed. Only applicable for type "phoneNumber" and "taxNumber".
 * @param {int} [props.formFieldColumSize=1] - The column count size of the form field from 1 up to 6.
 * @param {string} [props.placeholder=""] - The placeholder text for the input field.
 * @param {string} [props.textPrefix=""] - The text prefix for the input field. Usefull for adding a url prefix before the text.
 * @param {boolean} [props.valueReturnsTextPrefix=true] - This is a flag to know if the value of the input field should also include the text prefix.
 * @param {boolean} [props.resizable=false] - The flag to know if the input field should be resizable. Only applicable for type "text".
 * @param {int} [props.defaultSize] - The default size of the input field when resizable is true.
 * @param {string} [props.styling] - The styling for the component. ALL THE STYLING PROPS DOCS ARE IN THE FORMFIELD COMPONENT HEADER.
 * @param {string} [props.tagType] - The type of tag to display. Possible values are "hashtags" or "tags".
 * @param {Object} [props.isInvalid] - The flag to know if the input field is invalid.
 * @param {Object} [props.extraFields] - Extra fields that may be needed for the component for specific purposes.
 *
 * @returns {JSX.Element} The rendered FormField component.
 * for examples check route: /templates/forms and FormsTemplatePage.jsx
 */
const FormField = forwardRef((props, ref) => {
    const {
        valueCover = undefined,
        useValueMasking = false,
        allowAutoComplete = true,
        style,
        title,
        type,
        onEdit,
        requiredType: legacyRequiredType = "simple",
        appearance = "legacy",
        valuePresentation = "actual",
        requirement,
        interactionState,
        value = "",
        onTextChange,
        onTextBlur,
        onKeyDown,
        onFocus,
        onDropdownChange,
        tooltip,
        tooltipMaxWidth = "200px",
        hoverTooltip = "",
        leftContentUseFieldStateColor = false,
        rightContent,
        tagAddedColor = "#006D00",
        tagDropdownColor = "var(--text-icons-gray-100)",
        tagAddWhenEnterPressed = true,
        onTagsChanged,
        onTagRemoved,
        disableTagRemove = false,
        tagAsDropdown = false, // DO NOT USE THIS PROP (for custom purpose only)
        tagDropdownVisible = false, // DO NOT USE THIS PROP (for custom purpose only)
        className = "",
        minDate = "",
        maxDate = "",
        assistiveTextColorOverride = "",
        assistiveTextCheckbox,
        invalidAssistiveText = "Invalid Text",
        assistiveTextSpacing = true,
        multiLine = {
            isMultiLine: false,
            numberOfLines: undefined,
            autoExpand: false,
            maxLines: undefined
        },
        isDropdownMultiselect = false,
        isDropdownSearchable = false,
        isDragging = true,
        dropdownData = [],
        dropdownOnSingleItemClick,
        firstElementIsSelectAll = false,
        multidropdownOptions = {
            useDoneButton: true,
            useSelectAll: false,
            selectAllText: "Select All",
            selectAllDescription: undefined,
            placeholderTextFunction: undefined
        },
        dropdownSettings = {
            hasDescriptionFields: false,
            hasLockedFields: false,
            widthPercentage: undefined,
            hasMiddleFields: false,
            middleFieldKeyName: "middleField",
            mainFieldStyle: {},
            middleFieldStyle: {},
            descriptionStyle: {}
        },
        useDropdownPlaceholderAsTag = false,
        useDropdownPortal = false,
        dismissOnOutsideSvgClick = false,
        completeOnOutsideClick = false,
        phoneNumberCountryCode = "",
        phoneNumberCountryCodeName = "",
        phoneNumberSpacedEveryCount = 0,
        taxNumberCountryCode = "",
        placeholder,
        placeholderWidth = "auto",
        textPrefix = "",
        valueReturnsTextPrefix = true,
        formFieldColumSize = 1,
        onCountryCodeChange = () => {},
        onCurrentValueChange,
        onFormTextChange, // DO NOT USE THIS PROP (for FORM CARD USAGE ONLY), use onTextChange instead
        onFormTextBlur, // DO NOT USE THIS PROP (for FORM CARD USAGE ONLY), use onTextBlur instead
        onFormCardVerifyValidity, // DO NOT USE THIS PROP (for FORM CARD USAGE ONLY)
        onFormCardChanges, // DO NOT USE THIS PROP (for FORM CARD USAGE ONLY)
        resizable = false,
        defaultSize,
        styling,
        tagType,
        isInvalid,
        extraFields = {}
    } = props;

    const presentation = resolveFieldPresentation({ appearance, requirement, interactionState, requiredType: legacyRequiredType });
    const isPremiumBlocked = presentation.isPremium && presentation.isBlocked;
    // Reuse existing UI guards; retain the original type for validation.
    const requiredType = isPremiumBlocked ? "locked" : legacyRequiredType;

    let {
        assistiveText,
        leftContent,
        minLength = 0,
        maxLength = 500,
        lengthIgnoreSpaces = false,
        disableTooltip = false,
        disableAssistiveText = false
    } = props;

    const {
        backgroundColor,
        dropdownOverlayTop = "65px",
        dropdownOverlayWidth,
        dropdownOverlayStyle = {},
        requiredTypeColor,
        placeholder: stylingPlaceholder // Legacy... this was wrongly being used as actual placeholder property
    } = styling ?? {
        formFieldHeight: "37px",
        dropdownOverlayTop: "65px",
        dropdownOverlayStyle: {}
    };

    // #region STATES
    const inputRef = useRef();
    const tagInputRef = useRef();
    const formContainerRef = useRef();
    const portalContainerRef = useRef();
    const [currentValue, setCurrentValue] = useState(
        type !== "dropdown"
            ? value
            : dropdownData
                  ?.filter(item => item.selected)
                  .map(item => item.name)
                  .join(", ")
    );
    const [currentTags, setCurrentTags] = useState([]);
    const [dropdownSelectedData, setDropdownSelectedData] = useState([]);
    const outsideCompletionRef = useRef(null);
    // eslint-disable-next-line no-unused-vars
    const [inputID] = useState(props?.inputID || uuidv4());
    const requirementDescriptionId = presentation.isPremium && (presentation.requirement || presentation.interactionState === "locked") ? "field-requirement-" + inputID : undefined;
    const tooltipDescriptionId = presentation.isPremium && tooltip ? "field-tooltip-" + inputID : undefined;
    const dropdownPopupId = "field-options-" + inputID;
    const keyboardOpen = useRef(false);
    const premiumControlAria = presentation.isPremium ? {
        "aria-label": props["aria-label"] || (!props["aria-labelledby"] ? title : undefined),
        "aria-labelledby": props["aria-labelledby"],
        "aria-describedby": mergeDescriptionIds(props["aria-describedby"], requirementDescriptionId, tooltipDescriptionId),
        "aria-errormessage": props["aria-errormessage"],
        "aria-required": props["aria-required"],
        "aria-invalid": props["aria-invalid"],
        "aria-disabled": isPremiumBlocked ? true : props["aria-disabled"],
        tabIndex: isPremiumBlocked ? -1 : (props.tabIndex ?? (type === "dropdown" ? 0 : undefined))
    } : {};
    const [fieldIsInvalid, setFieldIsInvalid] = useState(false);
    const [typeInvalidAssistiveText, setTypeInvalidAssistiveText] = useState("");
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [currentDropdownData, setCurrentDropdownData] = useState(dropdownData);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [dropdownCheckedItemsValues, setDropdownCheckedItemsValues] = useState([]);

    const [isOutsideInvalid, setIsOutsideInvalid] = useState({
        isInvalid: false,
        invalidAssistiveText: ""
    });
    const [showingMaxLinesWarning, setShowingMaxLinesWarning] = useState(false);

    const [phoneNumberCountryCodeState, setPhoneNumberCountryCodeState] = useState(
        phoneNumberCountryCode.replace("+", "")
    );
    const [taxNumberCountryCodeState, setTaxNumberCountryCode] = useState(taxNumberCountryCode);

    //----- COMPUTED PROPS -----//
    if (type === "taxNumber") {
        const result = getTaxCountryCodeData(taxNumberCountryCodeState);
        minLength = result?.minLength || 0;
        maxLength = result?.maxLength || 500;
        lengthIgnoreSpaces = result?.lengthIgnoreSpaces || false;
    }
    if (type === "registrationNumber") {
        const result = getRegistrationCountryCodeData(extraFields?.countryCode);
        minLength = result?.minLength || 0;
        maxLength = result?.maxLength || 500;
        lengthIgnoreSpaces = result?.lengthIgnoreSpaces || false;
    }
    // #endregion

    // #region METHODS
    const updateTextareaHeight = () => {
        if (!multiLine?.autoExpand) return;
        document
            .querySelectorAll("textarea")
            .forEach(t => t.dispatchEvent(new Event("input", { bubbles: true, cancelable: true })));
    };

    const getDisplayValue = () => {
        if (useValueMasking) {
            //TODO: Check wich method to use
            // if (type === "email") return maskEmail(currentValue);
            // if (type === "phoneNumber") return maskPhoneNumber(currentValue);
            // if (type === "name") return maskName(currentValue);

            switch (type) {
                case "email":
                    return maskEmail(currentValue);
                case "phoneNumber":
                    return maskPhoneNumber(currentValue);
                case "text" || "number":
                    return maskName(currentValue);
                default:
                    return currentValue;
            }
        }
        return valueCover || currentValue;
    };

    const getTextColor = () => {
        if (styling?.textColorOverride) return styling?.textColorOverride;
        if (fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid) return "var(--text-icons-red-500)";
        if (requiredType === "locked" || requiredType === "mandatoryLocked") return "var(--text-icons-gray-300)";
        if (requiredType === "disabledSimple") return "var(--text-icons-green-500)";
        return "var(--text-icons-green-500)";
    };

    const getAssistiveTextColor = () => {
        if (isInvalid && invalidAssistiveText) {
            assistiveText = invalidAssistiveText;
            return "var(--text-icons-red-500)";
        }

        if ((assistiveTextColorOverride && !fieldIsInvalid && !isOutsideInvalid.isInvalid) || isInvalid)
            return assistiveTextColorOverride;
        if (isOutsideInvalid.isInvalid || isInvalid) {
            assistiveText = isOutsideInvalid.invalidAssistiveText;
            return "var(--text-icons-red-500)";
        } else if (fieldIsInvalid) {
            assistiveText = invalidAssistiveText;
            return "var(--text-icons-red-500)";
        }
        //if (requiredType === "locked" || requiredType === "mandatoryLocked" || requiredType === "disabledSimple") return "var(--text-icons-gray-300)";
        return "var(--text-icons-blue-500)";
    };

    const isValid = (highlightValidation = true) => {
        if (isOutsideInvalid.isInvalid) {
            if (highlightValidation) setFieldIsInvalid(true);
            return false;
        }
        if (isInvalid) {
            setFieldIsInvalid(true);
            return false;
        }
        if (
            (legacyRequiredType === "mandatory" || legacyRequiredType === "mandatoryLocked" || legacyRequiredType === "disabledSimple") &&
            ((type !== "tags" && !currentValue) || (type === "tags" && currentTags.length === 0))
        ) {
            if (highlightValidation) setFieldIsInvalid(true);
            return false;
        }
        if (!isTypeValid(false)) {
            if (highlightValidation) setFieldIsInvalid(true);
            return false;
        }
        if (highlightValidation) setFieldIsInvalid(false);
        return true;
    };

    const isTypeValid = (highlightValidation = true) => {
        const inputValue = lengthIgnoreSpaces ? currentValue?.toString()?.replaceAll(/\s/g, "") : currentValue;

        if (type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue) && inputValue?.length > 0) {
            if (highlightValidation) {
                setTypeInvalidAssistiveText("Invalid Email Format");
                setFieldIsInvalid(true);
            }
            return false;
        }
        if (type === "phoneNumber") {
            if (
                (legacyRequiredType !== "mandatory" && legacyRequiredType !== "mandatoryLocked") ||
                legacyRequiredType === "disabledSimple"
            )
                return true; //TODO: Review if we should have this line at the top of the isTypeValid function
            const currentValNoSpaces = inputValue?.toString()?.replaceAll(/\s/g, "");

            const validityResult = validatePhoneNumber(currentValNoSpaces, phoneNumberCountryCodeName);
            if (!validityResult.isValid) {
                if (highlightValidation) {
                    setTypeInvalidAssistiveText(validityResult.message);
                    setFieldIsInvalid(true);
                }
                return false;
            }
            if (!phoneNumberCountryCodeState && inputValue?.length > 0) {
                if (highlightValidation) {
                    setTypeInvalidAssistiveText("Invalid Country Code");
                    setFieldIsInvalid(true);
                }
                return false;
            }
            if (inputValue?.length > 0 && inputValue?.length < 6) {
                if (highlightValidation) {
                    setTypeInvalidAssistiveText("Invalid Phone Number");
                    setFieldIsInvalid(true);
                }
                return false;
            }
            setTypeInvalidAssistiveText("");
            setFieldIsInvalid(false);
            return true;
        }
        if (type === "taxNumber") {
            const currentValNoSpaces = inputValue?.toString()?.replaceAll(/\s/g, "");

            const validityResult = validateTaxNumber(currentValNoSpaces, taxNumberCountryCodeState);
            if (!validityResult.isValid) {
                if (highlightValidation) {
                    setTypeInvalidAssistiveText(validityResult.message);
                    setFieldIsInvalid(true);
                }
                return false;
            }

            setTypeInvalidAssistiveText("");
            setFieldIsInvalid(false);
            return true;
        } else if (type === "registrationNumber") {
            const currentValNoSpaces = inputValue?.toString()?.replaceAll(/\s/g, "");

            const validityResult = validateRegistrationNumber(currentValNoSpaces, extraFields?.countryCode);
            if (!validityResult.isValid) {
                if (highlightValidation) {
                    setTypeInvalidAssistiveText(validityResult.message);
                    setFieldIsInvalid(true);
                }
                return false;
            }

            setTypeInvalidAssistiveText("");
            setFieldIsInvalid(false);
            return true;
        }

        if (minLength > 0 && inputValue?.length < minLength) {
            if (highlightValidation) {
                setTypeInvalidAssistiveText(`Minimum ${minLength} characters`);
                setFieldIsInvalid(true);
            }
            return false;
        } else if (maxLength > 0 && inputValue?.length > maxLength) {
            if (highlightValidation) {
                setTypeInvalidAssistiveText(`Maximum ${maxLength} characters`);
                setFieldIsInvalid(true);
            }
            return false;
        } else {
            //if (highlightValidation) setFieldIsInvalid(false);
            setFieldIsInvalid(false);
        }

        setTypeInvalidAssistiveText("");
        return true;
    };

    const setOutsideInvalid = (isInvalid, invalidAssistiveText) => {
        setIsOutsideInvalid({ isInvalid, invalidAssistiveText: invalidAssistiveText || "" });
    };

    const textChange = e => {
        if (isPremiumBlocked) return;
        let currentText = e.target.value;
        if (multiLine?.isMultiLine && multiLine?.maxLines) {
            const lines = currentText.split("\n");
            if (lines.length > multiLine.maxLines) {
                currentText = lines.slice(0, multiLine.maxLines).join("\n");
                setShowingMaxLinesWarning(true);

                setTimeout(updateTextareaHeight, 100);
            }
        }

        if (type === "taxNumber" || type === "registrationNumber") {
            const digitsBetweenSpaces = taxNumberCountryCode === "NO" ? 3 : 2;

            currentText = currentText
                .replace(/[^0-9 ]/g, "")
                .replace(new RegExp(`(\\d{${digitsBetweenSpaces}})(?=\\d)`, "g"), "$1 ")
                .trim();

            // if DK use the same but without any spaces
            if (taxNumberCountryCode === "DK" || extraFields?.countryCode === "DK")
                currentText = currentText.replace(/ /g, "");
        }

        if (type === "phoneNumber" && phoneNumberSpacedEveryCount > 0) {
            //const digitsBetweenSpaces = phoneNumberCountryCodeName === "NO" || phoneNumberCountryCodeName === "DK" ? 4 : 99;

            currentText = currentText
                .replace(/[^0-9]/g, "")
                .replace(new RegExp(`(\\d{${phoneNumberSpacedEveryCount}})(?=\\d)`, "g"), "$1 ")
                .trim();
        }

        setIsOutsideInvalid({ isInvalid: false, invalidAssistiveText: "" });
        setCurrentValue(currentText);
        if (onTextChange) onTextChange(currentText);
        if (onFormTextChange) onFormTextChange(true);
        if (onFormCardChanges) onFormCardChanges(true);
    };

    const textBlur = e => {
        if (isPremiumBlocked) return;
        if (onTextBlur) onTextBlur(e.target.value);
        if (onFormTextBlur) onFormTextBlur(true);
        isTypeValid(true);
    };

    const textFocus = e => {
        if (isPremiumBlocked) return;
        if (onFocus) onFocus(e.target.value);
    };

    const outsideTagAdd = (tagName, value = undefined) => {
        if (type !== "tags") return;

        const event = {
            key: "Enter",
            target: { value: tagName }
        };

        onTagAdd(event, true, value);
    };

    const outsideTagRemove = tagName => {
        if (type !== "tags") return;

        const index = currentTags.findIndex(tag => tag.tagName === tagName);
        if (index >= 0) {
            handleTagRemove(index);
            setCurrentValue("");
        }
    };

    const onTagAdd = (e, isOutsideTagAdd = false, value = undefined) => {
        if (isPremiumBlocked && !isOutsideTagAdd) return;
        if (type !== "tags" || (!tagAddWhenEnterPressed && !isOutsideTagAdd)) return;
        if (type === "tags" && tagsChange && e.key === "Enter") {
            const name = e.target.value.trim();
            let formattedText = name.replace(/[^a-zA-Z0-9_]/g, ""); // Remove all special characters except underscores
            formattedText = tagType === "hashtags" ? `#${formattedText}` : name; // Add a single #
            if (!name) return;
            addTag(formattedText, value);
            setCurrentValue("");
            if (onFormCardChanges) onFormCardChanges(true);
        }
        if (fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid) isValid();
    };

    const onSelectAllClicked = (tagList = []) => {
        if (type !== "tags") return;
        const isAllSelected = tagList.every(tag => currentTags.some(currentTag => currentTag.tagName === tag?.name));

        if (!isAllSelected)
            setCurrentTags(tagList.map(tag => ({ bgColor: tagAddedColor, tagName: tag?.name, value: tag?.value })));
        else setCurrentTags([]);

        if (onFormCardChanges) onFormCardChanges(true);
    };
    const addTag = (tagName, value = undefined) => {
        if (type !== "tags") return;
        const newTag = { bgColor: tagAddedColor, tagName, value };
        const newTags = [...currentTags, newTag];
        tagsChange(newTags);
        if (onTagsChanged) onTagsChanged(newTags);
        if (onFormCardChanges) onFormCardChanges(true);
    };

    const tagsChange = newTags => {
        setCurrentTags(newTags);
    };

    const handleTagRemove = index => {
        if (disableTagRemove) return;

        const newTags = currentTags.filter((_item, idx) => idx !== index);
        tagsChange(newTags);
        if (onFormCardChanges) onFormCardChanges(true);
        if (onTagsChanged) onTagsChanged(newTags);

        const tag = currentTags[index];
        if (onTagRemoved) onTagRemoved(tag);
    };

    const onDroppedRemove = () => {
        setCurrentValue("");
        if (onTextChange) onTextChange("");
    };

    const handleTagFieldClick = () => {
        if (
            (tagInputRef.current && requiredType !== "locked" && requiredType !== "mandatoryLocked") ||
            requiredType === "disabledSimple"
        ) {
            try {
                tagInputRef.current.focus();
                if (onFocus) onFocus();
            } catch (error) {
                console.log("Error focusing tag input:", error);
            }
        }
    };

    const getValue = () => {
        if (type === "tags") return currentTags;
        if (type === "dropdown") return dropdownSelectedData;
        if (type === "phoneNumber")
            return "+" + phoneNumberCountryCodeState + " " + currentValue?.replaceAll(/\s/g, "");
        if (type === "taxNumber") return currentValue;
        if (textPrefix && valueReturnsTextPrefix) return addTextPrefix(currentValue);
        return currentValue;
    };

    const onDropdownClick = () => {
        if (
            type !== "dropdown" ||
            requiredType === "locked" ||
            requiredType === "mandatoryLocked" ||
            requiredType === "disabledSimple"
        )
            return;
        setDropdownVisible(!dropdownVisible);
    };

    const onDropdownSingleSelect = (data, allSelections) => {
        if (isPremiumBlocked) return;
        if (isDropdownMultiselect && allSelections) {
            const updatedAllSelections = Object.entries(allSelections).map(([key, item]) => {
                if (key !== data.value) return item;
                else return data.selected;
            });
            if (dropdownOnSingleItemClick) dropdownOnSingleItemClick(data, updatedAllSelections);
            return;
        }

        if (type === "phoneNumber") {
            setPhoneNumberCountryCodeState(data.value);
            onCountryCodeChange(data);
            setDropdownVisible(false);
            setShowDropdown(false);
            isTypeValid(false);
            //if (onTextChange) onTextChange("+" + data.value + " " + currentValue);
            //if (onFormTextChange) onFormTextChange("+" + data.value + " " + currentValue);
            if (onDropdownChange) onDropdownChange(data);
            return;
        }
        if (type === "taxNumber") {
            setTaxNumberCountryCode(data.value);
            onCountryCodeChange(data);
            setDropdownVisible(false);
            setShowDropdown(false);
            isTypeValid(true);
            if (onTextChange) onTextChange(data.value + " " + currentValue);
            if (onFormTextChange) onFormTextChange(data.value + " " + currentValue);
            if (onDropdownChange) onDropdownChange(data);
            return;
        }

        if (onTextChange) onTextChange(data);
        if (onFormTextChange) onFormTextChange(data);
        if (onDropdownChange) onDropdownChange(data);
        if (onFormCardChanges) onFormCardChanges(true);
        setCurrentValue(data.name);
        setDropdownSelectedData(data);
        setDropdownVisible(false);
    };

    const onDropdownMultiSelect = data => {
        if (isPremiumBlocked && dropdownVisible) return;
        if (!isDropdownMultiselect) return;

        const selectedData = data.filter(item => item.selected);

        /* const selectedTags = selectedData.map(item => {
            return { tagName: item.name };
        });
        setCurrentTags(selectedTags); */
        setDropdownSelectedData(selectedData);
        if (onTextChange) onTextChange(selectedData);
        if (onFormTextChange) onFormTextChange(selectedData);
        if (onFormCardChanges) onFormCardChanges(true);
        //if (onDropdownChange) onDropdownChange(data);
        //setCurrentValue(selectedData.map(item => item.name).join(", "));
        setCurrentValue(getDropdownLabelText(selectedData));
    };

    const dropdownAreAllSelected = () => {
        if (type !== "dropdown" || !isDropdownMultiselect) return false;

        const allSelected = dropdownData?.every(item => item?.selected);
        if (!multidropdownOptions?.allItems) return allSelected;

        const allItemsCount = multidropdownOptions?.allItems?.filter(item => !item?.locked)?.length || 0;
        const allSelectedCount = dropdownData?.filter(item => item?.selected)?.length || 0;

        return allItemsCount === allSelectedCount && allItemsCount > 0;
    };

    const removeTextPrefix = value => {
        const valueHasPrefix = value?.toLowerCase().startsWith(textPrefix.toLowerCase());

        if (valueHasPrefix) {
            if (!value) return "";
            else return value?.toLowerCase().replace(textPrefix.toLowerCase(), "");
        } else return value;
    };

    const addTextPrefix = value => {
        if (!value) return "";
        else return textPrefix + value;
    };

    const handleTextPrefixClick = () => {
        if (
            (inputRef.current && requiredType !== "locked" && requiredType !== "mandatoryLocked") ||
            requiredType === "disabledSimple"
        )
            inputRef.current.focus();
    };

    const handleDrop = e => {
        e.preventDefault();
        if (type !== "droppable" || requiredType === "locked" || currentValue) return;
        const data = e.dataTransfer.getData("text/plain");
        textChange({ target: { value: data } });
        setIsDragOver(false);
    };

    const handleDragOver = e => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const getDropdownLabelText = data => {
        if (type !== "dropdown") return;

        let result =
            data?.length === 0
                ? "--"
                : data
                      ?.filter(item => item.selected)
                      .map(item => item.name)
                      .join(", ");

        if (data?.length === 0 && isDropdownMultiselect && multidropdownOptions?.labelNoneSelected !== undefined)
            return multidropdownOptions?.labelNoneSelected;

        if (isDropdownMultiselect && multidropdownOptions?.placeholderTextFunction) {
            const { placeholderTextFunction, allItems, labelAllSelected, labelNoneSelected } = multidropdownOptions;

            return placeholderTextFunction(data, allItems, labelAllSelected, labelNoneSelected) || result;
        }
        if (isDropdownMultiselect && firstElementIsSelectAll) {
            //check if data contains value="selectAll" and is selected
            const selectAll = data.find(item => item.value === "selectAll");
            if (selectAll && selectAll.selected) result = selectAll.name;
        }

        return result;
    };

    const getPlaceholderColorForDropdown = () => {
        if (!isDropdownMultiselect) {
            if (currentValue) return null;
            if (requiredType === "locked" || requiredType === "mandatoryLocked")
                return "var(--text-icons-gray-300) !important";
            return "var(--text-icons-gray-200) !important";
        }
        const noneSelected = dropdownSelectedData?.every(item => !item.selected);
        if (noneSelected) return "var(--text-icons-gray-300)";
        else return null;
    };
    // #endregion

    // #region EFFECTS
    const [portalPosition, setPortalPosition] = useState(null); // Portal dropdown positioning � only active when useDropdownPortal is true
    useEffect(() => {
        if (!useDropdownPortal || !dropdownVisible || !formContainerRef.current) {
            setPortalPosition(null);
            return;
        }

        // Calculate initial position
        const rect = formContainerRef.current.getBoundingClientRect();
        setPortalPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width
        });

        // Close dropdown on external scroll (same behavior as native date picker)
        const handleScrollIntent = e => {
            // Don't close if scrolling inside the dropdown itself (e.g. scrolling the options list)
            if (portalContainerRef.current && portalContainerRef.current.contains(e.target)) return;
            setDropdownVisible(false);
        };

        // Capture phase catches scroll in any ancestor element
        window.addEventListener("scroll", handleScrollIntent, true);
        window.addEventListener("resize", handleScrollIntent);

        return () => {
            window.removeEventListener("scroll", handleScrollIntent, true);
            window.removeEventListener("resize", handleScrollIntent);
        };
    }, [useDropdownPortal, dropdownVisible]);

    useEffect(() => {
        if (!showingMaxLinesWarning) return;

        Library.showToastMessage({
            type: "warning",
            message: "The maximum number of lines has been reached!",
            autoClose: 2000,
            onClose: () => setShowingMaxLinesWarning(false)
        });
        updateTextareaHeight();
    }, [showingMaxLinesWarning]);

    useEffect(() => {
        if (type === "dropdown") {
            if (isDropdownMultiselect) {
                /* TODO: Implement multiselect dropdown value handling */
            } else {
                //const selectedData = dropdownData.map(item => ({ ...item, selected: item.name === value }));
                const selectedData = dropdownData.find(item => item.name === value);
                setDropdownSelectedData(selectedData);
                setCurrentValue(selectedData); //TODO: Or setCurrentValue(selectedData?.name) ?
            }
        }
        if (type === "tags") {
            if (!value) {
                setCurrentTags([]);
                return;
            }

            //check for differences in the tags array
            const isTagNamesDifferent =
                value?.length !== currentTags?.length ||
                value?.some((tag, index) => tag !== currentTags[index]?.tagName);

            if (!isTagNamesDifferent) {
                return;
            }

            try {
                const newTags = value?.map(tag => ({ bgColor: tagAddedColor, tagName: tag }));
                setCurrentValue("");
                setCurrentTags(newTags);
                if (onTagsChanged) onTagsChanged(newTags);
                return;
            } catch (error) {
                console.log("Error setting tags", value);
                return;
            }
        }
        if (textPrefix && valueReturnsTextPrefix) setCurrentValue(removeTextPrefix(value));
        else setCurrentValue(value);
    }, [value]);

    useEffect(() => {
        if (type !== "dropdown") return;

        setCurrentDropdownData(dropdownData);
        if (isDropdownMultiselect) {
            setDropdownSelectedData(dropdownData.filter(item => item.selected));

            //dropdownCheckedItemsValues
            const checkedItemsValues = dropdownData.filter(item => item.selected).map(item => item.value);
            setDropdownCheckedItemsValues(checkedItemsValues);

            return;
        } else setDropdownSelectedData(dropdownData.find(item => item.selected));

        /* setCurrentValue(
            dropdownData
                ?.filter(item => item.selected)
                .map(item => item.name)
                .join(", ")
        ); */
        setCurrentValue(getDropdownLabelText(dropdownData));

        if (!dropdownData.find(item => item.selected) && value) {
            const selected = dropdownData.find(item => item.name === value);
            if (selected) {
                setDropdownSelectedData(selected);
                setCurrentValue(selected.name);
            }
        }
    }, [dropdownData]);

    useEffect(() => {
        //if hidding dropdown and type is dropdown and isMultiselect then notify dropdown changes and form card changes
        if (
            dropdownVisible === false &&
            type === "dropdown" &&
            isDropdownMultiselect &&
            Array.isArray(dropdownSelectedData)
        ) {
            const dropdownDataWithSelections = dropdownData.map(item => {
                return { ...item, selected: dropdownSelectedData.some(selected => selected.value === item.value) };
            });

            if (onDropdownChange) onDropdownChange(dropdownSelectedData, dropdownDataWithSelections);
            if (onFormCardChanges) onFormCardChanges(true);
        }
    }, [dropdownVisible]);

    useEffect(() => {
        if (type === "phoneNumber") {
            setPhoneNumberCountryCodeState(phoneNumberCountryCode.replace("+", ""));
        }
        if (type === "taxNumber") {
            setTaxNumberCountryCode(taxNumberCountryCode);
        }
    }, [phoneNumberCountryCode, taxNumberCountryCode]);

    useEffect(() => {
        if (onCurrentValueChange && type !== "tags") onCurrentValueChange(currentValue);
        if (multiLine?.isMultiLine && multiLine?.autoExpand) setTimeout(updateTextareaHeight, 100);
    }, [currentValue]);

    useEffect(() => {
        if (onCurrentValueChange) onCurrentValueChange(currentTags);

        //-->if (onFormCardChanges) onFormCardChanges(true);
        if (onFormCardVerifyValidity) onFormCardVerifyValidity();
    }, [currentTags]);

    useEffect(() => {
        if (dropdownVisible) {
            const closeDropdown = event => {
                const target = typeof event.target?.closest === "function" ? event.target : event.target?.parentElement;
                if (typeof target?.closest !== "function") return;
                const isSVG = event.target?.className?.toString?.() === "[object SVGAnimatedString]";

                if (
                    !target.closest(".dropdownContainer-" + inputID) &&
                    !target.closest(".Frame-" + inputID) &&
                    !target.closest(".dropdown-label-" + inputID) &&
                    (dismissOnOutsideSvgClick || !isSVG)
                ) {
                    const complete = outsideCompletionRef.current;
                    outsideCompletionRef.current = null;
                    setDropdownVisible(false);
                    complete?.();
                }
            };

            document.addEventListener("click", closeDropdown);
            return () => document.removeEventListener("click", closeDropdown);
        }
    }, [dropdownVisible]);

    useEffect(() => {
        if (onFormCardVerifyValidity) onFormCardVerifyValidity();
    }, [isOutsideInvalid]);

    useEffect(() => {
        if (!multiLine?.isMultiLine || !multiLine?.autoExpand) return;

        const textareas = document.querySelectorAll("textarea.autoResize");

        textareas.forEach(textarea => {
            textarea.style.height = textarea.scrollHeight + "px";
            textarea.style.overflowY = "hidden";

            const handleInput = function () {
                this.style.height = "auto";
                this.style.height = this.scrollHeight + "px";
            };

            textarea.addEventListener("input", handleInput);

            // Clean up the event listener on unmount
            return () => {
                textarea.removeEventListener("input", handleInput);
            };
        });
    }, []);

    useEffect(() => {
        // if is invalid, recheck if it is no longer invalid and remove the invalid red state
        if (
            (fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid) &&
            ["text", "number", "email", "phoneNumber", "taxNumber"].includes(type)
        ) {
            isValid();
        }
    }, [currentValue]);
    // #endregion

    // #region COMPUTED VALUES
    const required = legacyRequiredType === "mandatory" || legacyRequiredType === "mandatoryLocked";
    const disabled =
        requiredType === "locked" ||
        requiredType === "mandatoryLocked" ||
        type === "droppable" ||
        type === "dropdown" ||
        requiredType === "disabledSimple";
    const textColor = getTextColor();

    if (type === "phoneNumber") {
        leftContent = LeftContentPhoneNumber({
            showDropdown,
            setShowDropdown,
            setCurrentDropdownData,
            setDropdownVisible,
            phoneNumberCountryCode: phoneNumberCountryCodeState,
            fieldIsInvalid: fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid,
            isDisabled: disabled
        });
    }

    if (type === "taxNumber") {
        leftContent = LeftContentTaxNumber({
            taxNumberCountryCodeState,
            fieldIsInvalid: fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid,
            isDisabled: disabled
        });
    }

    if (type === "email") {
        leftContent = <img className="mailIcon" src={Mail05Icon} alt="" />;
    }

    const composedMultidropdownOptions = type === "dropdown" && isDropdownMultiselect ? {
        ...multidropdownOptions,
        onDoneClick: () => {
            if (isPremiumBlocked) return;
            setDropdownVisible(false);
            multidropdownOptions?.onDoneClick?.(dropdownSelectedData);
        }
    } : multidropdownOptions;

    // The document listener keeps its original lifetime; this ref supplies the latest pending selection.
    outsideCompletionRef.current = completeOnOutsideClick && dropdownVisible && type === "dropdown" &&
        isDropdownMultiselect && !["locked", "mandatoryLocked", "disabledSimple"].includes(requiredType) ? () => {
            // Reuse the actual DONE eligibility (including search/locked rows), rather than a second validation rule.
            const done = document.querySelector(".dropdownContainer-" + inputID + " .btn-large");
            if (multidropdownOptions?.useDoneButton && done && !done.classList.contains("disabled"))
                composedMultidropdownOptions.onDoneClick();
        } : null;

    const infoIcon = (
        <InfoCircle className={`Icon ${requiredType} text-align-center ${
            fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid ? "text-red-500" : ""
        }`} aria-hidden="true" />
    );

    if (!assistiveText && !props?.invalidAssistiveText) disableAssistiveText = true;
    if (!tooltip && !title) disableTooltip = true;
    // #endregion

    useImperativeHandle(ref, () => ({
        isValid,
        getValue,
        setOutsideInvalid,
        outsideTagAdd,
        outsideTagRemove,
        onSelectAllClicked
    }));

    // Premium keyboard adapter delegates activation to the existing dropdown callbacks.
    // TAB remains native; legacy dropdown behavior is untouched.
    // A composing combobox adapter owns its row semantics, focus and bubbling keys.
    const hasDropdownKeyboardAdapter = () => inputRef.current?.getAttribute("role") === "combobox";
    const premiumDropdownTargets = () => {
        const popup = document.getElementById(dropdownPopupId);
        if (!popup) return [];
        const rows = [...popup.querySelectorAll(".dropdown-item")];
        rows.forEach(row => {
            const blocked = !!dropdownSettings?.hasLockedFields && row.classList.contains("hover-not-allowed");
            row.tabIndex = -1;
            row.setAttribute("role", "button");
            row.setAttribute("aria-disabled", String(blocked));
        });
        const actions = [...popup.querySelectorAll(".btn-large.enabled")];
        actions.forEach(action => { action.tabIndex = 0; action.setAttribute("role", "button"); });
        return [...rows.filter(row => row.getAttribute("aria-disabled") !== "true"), ...actions];
    };
    useEffect(() => {
        if (!presentation.isPremium || type !== "dropdown" || !dropdownVisible || isPremiumBlocked || hasDropdownKeyboardAdapter()) return;
        const targets = premiumDropdownTargets();
        // A portal mounts only after its positioning effect has run.
        // Keep the keyboard request until an eligible mounted target can receive focus.
        if (keyboardOpen.current && targets.length) {
            keyboardOpen.current = false;
            targets[0]?.focus();
        }
    });
    const onPremiumDropdownKeyDown = event => {
        if (!presentation.isPremium || type !== "dropdown" || isPremiumBlocked || event.key === "Tab" || hasDropdownKeyboardAdapter()) return;
        const primary = event.target === inputRef.current;
        if (event.key === "Escape" && dropdownVisible) {
            event.preventDefault();
            setDropdownVisible(false); setShowDropdown(false); inputRef.current?.focus();
            return;
        }
        if (primary && !dropdownVisible && ["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault(); keyboardOpen.current = true; onDropdownClick(); return;
        }
        if (!dropdownVisible) return;
        const targets = premiumDropdownTargets();
        if (["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            const index = targets.indexOf(event.target.closest(".dropdown-item, .btn-large.enabled"));
            const next = event.key === "ArrowDown" ? (index + 1) % targets.length :
                (index < 0 ? targets.length - 1 : (index - 1 + targets.length) % targets.length);
            targets[next]?.focus();
        } else if (["Enter", " "].includes(event.key) && targets.includes(event.target)) {
            event.preventDefault(); event.target.click();
            if (!isDropdownMultiselect || event.target.matches(".btn-large.enabled")) inputRef.current?.focus();
        }
    };

    // #region RENDER
    const DropdownContainer = presentation.isPremium ? PremiumDropdownSurface : "div";
    return (
        <StyledElems
            formFieldHeight={styling?.formFieldHeight ?? (presentation.isPremium ? premiumTheme.fieldHeight : "37px")}
            className={`FormField ${className} ${requiredType}`}
            hidden={presentation.isPremium ? props.hidden : undefined}
            aria-hidden={presentation.isPremium ? props["aria-hidden"] : undefined}
            data-appearance={presentation.isPremium ? "premium" : undefined}
            data-interaction-state={presentation.isPremium ? presentation.interactionState : undefined}
            data-value-presentation={presentation.isPremium ? (valuePresentation === "suggested" ? "suggested" : "actual") : undefined}
            onKeyDown={presentation.isPremium ? onPremiumDropdownKeyDown : undefined}
            style={{
                ...style,
                gridColumn: `span ${formFieldColumSize}`,
                width: styling?.formFieldWidth ?? style?.width,
                maxWidth: styling?.formFieldMaxWidth ?? style?.maxWidth,
                minWidth: styling?.formFieldMinWidth ?? style?.minWidth
            }}>
            {tooltipDescriptionId && <span className="premium-field-description" id={tooltipDescriptionId}>{tooltip}</span>}
            <div className="FormComponentContainer" ref={useDropdownPortal ? formContainerRef : undefined}>
                <div className={`Frame51 ${disableTooltip && !(presentation.isPremium && (presentation.interactionState === "locked" || ["mandatory", "recommended"].includes(presentation.requirement))) ? "hidden" : ""}`}>
                    {tooltip && (
                        <Tooltip
                            title={tooltip}
                            trigger={presentation.isPremium ? ["hover", "focus"] : undefined}
                            color="#10141b"
                            overlayInnerStyle={{ textAlign: "center" }}
                            overlayStyle={{ fontSize: "0.75rem", maxWidth: tooltipMaxWidth, textAlign: "center" }}>
                            {presentation.isPremium ? <span className="premium-info-trigger" tabIndex={-1} aria-hidden="true">{infoIcon}</span> : infoIcon}
                        </Tooltip>
                    )}
                    {!tooltip && <InfoCircle className="Icon  hidden" aria-hidden="true"></InfoCircle>}
                    <div
                        className={`Title ${
                            fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid
                                ? "text-red-500"
                                : requiredType === "disabledSimple"
                                  ? "text-gray-200"
                                  : requiredType === "locked" || requiredType === "mandatoryLocked"
                                    ? "text-gray-300"
                                    : ""
                        }`}>
                        {title}
                    </div>
                    {onEdit && requiredType !== "locked" && requiredType !== "disabledSimple" && (
                        <div className="field-edit-option" onClick={onEdit}>
                            <img src={Edit03} alt="Edit" />
                        </div>
                    )}
                    {presentation.isPremium && <FieldRequirement requirement={presentation.requirement} interactionState={presentation.interactionState} descriptionId={requirementDescriptionId} />}
                </div>
                <div
                    className={`InputSelectorInverted ${
                        type !== "droppable"
                            ? ""
                            : requiredType === "locked"
                              ? ""
                              : currentValue
                                ? "dropped"
                                : isDragOver
                                  ? "drag-over"
                                  : isDragging && "dragged"
                    } ${resizable ? "resizable-content-horizontal" : ""}`}
                    style={{
                        width: defaultSize ? defaultSize : undefined,
                        boxShadow: type === "droppable" && isInvalid && "0px 0px 0px 1px var(--text-icons-red-500)"
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}>
                    <Tooltip title={hoverTooltip || (presentation.isPremium ? tooltip : "")} trigger={presentation.isPremium ? ["hover", "focus"] : undefined} color="#10141b" overlayInnerStyle={{ textAlign: "center" }}>
                        <div
                            className={`Search Frame48 ${
                                fieldIsInvalid || isOutsideInvalid.isInvalid || isInvalid
                                    ? "invalid"
                                    : requiredType === "mandatory" &&
                                        type === "phoneNumber" &&
                                        phoneNumberCountryCodeState !== ""
                                      ? "simple"
                                      : (props.type === "tags" && currentTags?.length === 0) ||
                                          (props.type !== "tags" && !currentValue) ||
                                          requiredType === "locked" ||
                                          requiredType === "mandatoryLocked" ||
                                          requiredType === "disabledSimple" ||
                                          requiredType === "none" ||
                                          (requiredType === "mandatory" &&
                                              props.type === "dropdown" &&
                                              props.isDropdownMultiselect &&
                                              dropdownSelectedData?.length === 0)
                                        ? requiredType
                                        : "simple"
                            } ${
                                disabled && type === "droppable"
                                    ? ""
                                    : disabled && type === "dropdown"
                                      ? "cursor-pointer"
                                      : disabled
                                        ? "disabled"
                                        : ""
                            } ${tagAsDropdown ? "tagAsDropdown" : ""} input-${inputID}`}
                            onClick={handleTagFieldClick}
                            style={{
                                height: "unset",
                                minHeight: styling?.formFieldHeight ?? "37px",
                                borderLeft: requiredTypeColor ? `5px solid ${requiredTypeColor}` : "",
                                ...(props?.styling?.inputStyles ?? {})
                            }}>
                            {leftContent !== undefined && (
                                <>
                                    <div
                                        className={`Frame48LeftContent Frame-${inputID}${leftContentUseFieldStateColor ? " useFieldStateColor" : ""}`}
                                        style={{
                                            ...(leftContentUseFieldStateColor ? { "--left-content-field-color": textColor } : {}),
                                            cursor:
                                                requiredType === "locked" ||
                                                requiredType === "mandatoryLocked" ||
                                                requiredType === "disabledSimple"
                                                    ? "not-allowed"
                                                    : type === "date" || type === "datetime-local"
                                                      ? "pointer"
                                                      : "default"
                                        }}
                                        onClick={() =>
                                            requiredType !== "locked" && (type === "date" || type === "datetime-local")
                                                ? inputRef.current.showPicker()
                                                : null
                                        }>
                                        {leftContent}
                                    </div>
                                </>
                            )}
                            {type === "tags" && (
                                <>
                                    <div
                                        className={`Frame48TagsContainer ${
                                            requiredType !== "locked" &&
                                            requiredType !== "mandatoryLocked" &&
                                            requiredType !== "disabledSimple"
                                                ? tagAsDropdown
                                                    ? "cursor-pointer"
                                                    : "cursor-text"
                                                : ""
                                        }`}>
                                        {currentTags?.map((tag, index) => (
                                            <TagComponent
                                                key={uuidv4()}
                                                index={index}
                                                bgColor={tag?.bgColor ?? tagAddedColor}
                                                tagName={tag?.tagName ?? "--"}
                                                hideRemoveButton={tag?.hideRemoveButton || disableTagRemove || isPremiumBlocked}
                                                onRemoveClick={() => { if (!isPremiumBlocked) handleTagRemove(index); }}
                                                tagStyles={styling?.tagStyles}
                                            />
                                        ))}
                                        <input
                                            autoComplete={allowAutoComplete ? "on" : "off"}
                                            type="text"
                                            className={`TagsInput ${tagAsDropdown ? "hidden" : ""}`}
                                            {...premiumControlAria}
                                            disabled={isPremiumBlocked || undefined}
                                            value={currentValue}
                                            onChange={textChange}
                                            onKeyUp={onTagAdd}
                                            onBlur={textBlur}
                                            onFocus={textFocus}
                                            ref={tagInputRef}
                                            placeholder={
                                                stylingPlaceholder
                                                    ? stylingPlaceholder
                                                    : tagAddWhenEnterPressed
                                                      ? "Press enter to add tags"
                                                      : "Write and select a tag "
                                            }
                                            style={{ width: placeholderWidth }}
                                        />
                                    </div>

                                    {tagAsDropdown && (
                                        <ChevronDownSVG
                                            className={`dropdownArrow tagAsDropdown ${
                                                tagDropdownVisible ? "active" : ""
                                            }`}
                                            style={{ color: tagDropdownColor }}
                                            color={tagDropdownColor || "#D1D1D1"}
                                        />
                                    )}
                                </>
                            )}
                            {type === "dropdown" && (
                                <>
                                    <div
                                        className={`Frame48Input Frame-${inputID}`}
                                        {...premiumControlAria}
                                        ref={presentation.isPremium ? inputRef : undefined}
                                        role={presentation.isPremium ? "button" : undefined}
                                        aria-expanded={presentation.isPremium ? dropdownVisible : undefined}
                                        aria-controls={presentation.isPremium && dropdownVisible ? dropdownPopupId : undefined}
                                        style={{
                                            cursor:
                                                requiredType === "locked" ||
                                                requiredType === "mandatoryLocked" ||
                                                requiredType === "disabledSimple"
                                                    ? "not-allowed"
                                                    : "pointer",
                                            color: textColor,
                                            height: styling?.formFieldHeight ?? "37px",
                                            display: "flex",
                                            alignItems: "center",
                                            position: "relative",
                                            background: backgroundColor,
                                            borderTopRightRadius: "4px",
                                            borderBottomRightRadius: "4px"
                                        }}
                                        onClick={onDropdownClick}>
                                        {/* HANDLING WHEN SHOULD BE A TAG */}
                                        {useDropdownPlaceholderAsTag &&
                                        (!dropdownAreAllSelected() || dropdownVisible) ? (
                                            <TagComponent
                                                className="ml-12px mr-32px"
                                                bgColor="rgba(0, 187, 0, 0.1)"
                                                textColor="#00BB00"
                                                xTextColor="#D1D1D1"
                                                tagName={currentValue || stylingPlaceholder}
                                                useRoundedVariant={true}
                                                hideRemoveButton={!isDropdownMultiselect || isPremiumBlocked}
                                                onRemoveClick={() => {
                                                    if (isPremiumBlocked || !isDropdownMultiselect) return;

                                                    const data = dropdownData.map(item => {
                                                        return { ...item, selected: !item?.locked };
                                                    });

                                                    setDropdownSelectedData(data);
                                                    onDropdownMultiSelect(data);
                                                    setCurrentDropdownData(data);

                                                    const checkedItemValuesTmp = data
                                                        .filter(item => item.selected)
                                                        .map(item => item.value);

                                                    setDropdownCheckedItemsValues(checkedItemValuesTmp);

                                                    const selectedItems = data.filter(item => item.selected);

                                                    if (onDropdownChange) onDropdownChange(selectedItems, data);
                                                    if (onFormCardChanges) onFormCardChanges(true);

                                                    //onDropdownChange(data);
                                                }}
                                            />
                                        ) : (
                                            <label
                                                className={`dropdown-label-${inputID} text-one-line ${
                                                    !currentValue ? "placeholder" : ""
                                                }`}
                                                onClick={onDropdownClick}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    alignContent: "center",
                                                    textAlign: "left",
                                                    overflow: "hidden",
                                                    paddingRight: "30px",
                                                    paddingLeft: "12px",
                                                    color: getPlaceholderColorForDropdown()
                                                }}>
                                                {currentValue || stylingPlaceholder || placeholder}
                                            </label>
                                        )}

                                        <div
                                            className={`dropdownArrow ${dropdownVisible ? "active" : ""} ${
                                                requiredType === "locked" ||
                                                requiredType === "mandatoryLocked" ||
                                                requiredType === "disabledSimple"
                                                    ? "disabled"
                                                    : ""
                                            }`}>
                                            <ChevronDownSVG
                                                color={
                                                    requiredType === "locked" ||
                                                    requiredType === "mandatoryLocked" ||
                                                    requiredType === "disabledSimple"
                                                        ? "var(--text-icons-gray-300)"
                                                        : "var(--text-icons-gray-100)"
                                                }
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {type !== "tags" && type !== "dropdown" && (
                                <div
                                    className={`${type === "date" && !currentValue ? "no-date" : "has-date"}`}
                                    style={{
                                        position: "relative",
                                        width: "100%",
                                        flexShrink: "1",
                                        overflow: "hidden"
                                    }}>
                                    <div className={`inputFieldContainer ${multiLine?.autoExpand ? "pr-0" : ""}`}>
                                        {textPrefix && (
                                            <div className="textPrefix" onClick={handleTextPrefixClick}>
                                                {textPrefix}
                                            </div>
                                        )}
                                        <CustomInputField
                                            {...premiumControlAria}
                                            multilineoptions={multiLine}
                                            id={`myDateInput-${inputID}`}
                                            ref={inputRef}
                                            type={type === "phoneNumber" || type === "taxNumber" ? "text" : type}
                                            subType={type}
                                            min={minDate}
                                            max={maxDate}
                                            placeholder={placeholder}
                                            onBlur={textBlur}
                                            onFocus={textFocus}
                                            disabled={disabled}
                                            required={required}
                                            value={getDisplayValue() || ""}
                                            onChange={textChange}
                                            minLength={minLength}
                                            maxLength={maxLength}
                                            lengthIgnoreSpaces={lengthIgnoreSpaces}
                                            //onKeyUp={onTagAdd}
                                            onKeyDown={isPremiumBlocked ? undefined : onKeyDown}
                                            autoComplete={allowAutoComplete ? "on" : "off"}
                                            className={`Frame48Input ${
                                                type === "date" || type === "datetime-local"
                                                    ? !currentValue
                                                        ? "datepicker no-date"
                                                        : "datepicker has-date"
                                                    : ""
                                            }`}
                                            style={{
                                                //minWidth: "100px",
                                                height: styling?.formFieldHeight ?? "37px",
                                                color: textColor,
                                                cursor:
                                                    type === "dropdown"
                                                        ? "pointer"
                                                        : type === "droppable"
                                                          ? "default"
                                                          : disabled
                                                            ? "not-allowed"
                                                            : "text",
                                                textOverflow: "ellipsis"
                                            }}
                                        />
                                    </div>
                                    {(type === "date" || type === "datetime-local") && props?.datePickerPlaceholder && (
                                        <label
                                            htmlFor={`myDateInput-${inputID}`}
                                            className="placeholder"
                                            style={{ display: "none", color: textColor, cursor: "pointer" }}
                                            onClick={() => inputRef.current.showPicker()}>
                                            {props?.datePickerPlaceholder}
                                        </label>
                                    )}
                                    {type === "droppable" && requiredType !== "locked" && currentValue && (
                                        <div className="droppedRemove" onClick={onDroppedRemove}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="17"
                                                viewBox="0 0 16 17"
                                                fill="none">
                                                <path
                                                    d="M11.5286 12.9714C11.7889 13.2318 12.211 13.2318 12.4714 12.9714C12.7317 12.7111 12.7317 12.289 12.4714 12.0286L8.94277 8.50004L12.4714 4.97145C12.7317 4.7111 12.7317 4.28899 12.4714 4.02864C12.211 3.76829 11.7889 3.76829 11.5286 4.02864L7.99996 7.55723L4.47136 4.02864C4.21101 3.76829 3.7889 3.76829 3.52855 4.02864C3.2682 4.28899 3.2682 4.7111 3.52855 4.97144L7.05715 8.50004L3.52855 12.0286C3.2682 12.289 3.2682 12.7111 3.52855 12.9714C3.7889 13.2318 4.21101 13.2318 4.47136 12.9714L7.99996 9.44285L11.5286 12.9714Z"
                                                    fill="#D1D1D1"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            )}
                            {rightContent !== undefined && (
                                <div className="Frame48RightContent" style={{ color: getTextColor() }}>
                                    {rightContent}
                                </div>
                            )}
                        </div>
                    </Tooltip>
                </div>

                {(type === "dropdown" || showDropdown) &&
                    (() => {
                        const dropdownContent = (
                            <DropdownOverlay
                                isSearchable={isDropdownSearchable || type === "phoneNumber"}
                                data={dropdownSettings?.pinnedOption && !isDropdownMultiselect
                                    ? currentDropdownData.filter(item => item.value !== dropdownSettings.pinnedOption.value)
                                    : currentDropdownData}
                                pinnedOption={!isDropdownMultiselect ? dropdownSettings?.pinnedOption : undefined}
                                onSingleItemClick={onDropdownSingleSelect}
                                isChangeColor={!isDropdownMultiselect && isDropdownSearchable}
                                overlayType={isDropdownMultiselect ? OverlayType.MULTI_SELECT : OverlayType.UNI_SELECT}
                                multiSelectData={onDropdownMultiSelect}
                                isVisible={dropdownVisible && !isPremiumBlocked}
                                style={{
                                    width: "100%",
                                    top: useDropdownPortal ? 0 : dropdownOverlayTop,
                                    zIndex: 1000,
                                    ...dropdownOverlayStyle
                                }}
                                checkedItemValues={isDropdownMultiselect ? dropdownCheckedItemsValues : undefined}
                                hasDescriptionFields={dropdownSettings?.hasDescriptionFields}
                                descriptionFieldKeyName={dropdownSettings?.descriptionFieldKeyName}
                                hasMiddleFields={dropdownSettings?.hasMiddleFields}
                                mainFieldStyle={dropdownSettings?.mainFieldStyle}
                                middleFieldKeyName={dropdownSettings?.middleFieldKeyName}
                                middleFieldStyle={dropdownSettings?.middleFieldStyle}
                                descriptionStyle={dropdownSettings?.descriptionStyle}
                                hasLockedFields={dropdownSettings?.hasLockedFields}
                                widthPercentage={dropdownSettings?.widthPercentage}
                                multidropdownOptions={composedMultidropdownOptions}
                                visibleOptionCount={dropdownSettings?.visibleOptionCount ?? (presentation.isPremium ? 7 : undefined)}
                                scrollableSize={isDropdownMultiselect && isDropdownSearchable ? 324 : 224}
                            />
                        );

                        if (useDropdownPortal) {
                            if (!portalPosition) return <></>;

                            return createPortal(
                                <DropdownContainer
                                    ref={portalContainerRef}
                                    className={`dropdownContainer-${inputID}`}
                                id={presentation.isPremium ? dropdownPopupId : undefined}
                                    style={{
                                        position: "fixed",
                                        top: portalPosition.top + (formContainerRef.current?.offsetHeight || 0),
                                        left: portalPosition.left,
                                        width: dropdownOverlayWidth || portalPosition.width,
                                        zIndex: 99999
                                    }}>
                                    {dropdownContent}
                                </DropdownContainer>,
                                document.body
                            );
                        }

                        return (
                            <DropdownContainer
                                className={`dropdownContainer-${inputID}`}
                                id={presentation.isPremium ? dropdownPopupId : undefined}
                                style={{ position: "absolute", width: "100%", zIndex: "9999" }}>
                                {dropdownContent}
                            </DropdownContainer>
                        );
                    })()}
                {assistiveTextSpacing && (
                    <div
                        className={`Frame50 ${disableAssistiveText ? "hidden" : ""}${assistiveTextCheckbox ? " withCheckbox" : ""}`}
                        style={{ color: getAssistiveTextColor() }}>
                        {assistiveTextCheckbox && (
                            <button type="button" className="AssistiveTextCheckbox" role="checkbox"
                                aria-label={assistiveTextCheckbox.ariaLabel ?? assistiveText}
                                aria-checked={!!assistiveTextCheckbox.isChecked}
                                disabled={!!assistiveTextCheckbox.disabled || isPremiumBlocked}
                                onClick={assistiveTextCheckbox.onCheckboxClick}>
                                <CheckboxVector isChecked={!!assistiveTextCheckbox.isChecked}
                                    disabled={!!assistiveTextCheckbox.disabled || isPremiumBlocked} />
                            </button>
                        )}
                        <div
                            className="AssistiveText"
                            dangerouslySetInnerHTML={{ __html: typeInvalidAssistiveText || assistiveText }}></div>
                    </div>
                )}
            </div>
        </StyledElems>
    );
    // #endregion
});

export default FormField;

// #region SMALL COMPONENTS
const CustomInputField = forwardRef((props, ref) => {
    const { type, id, multilineoptions, lengthIgnoreSpaces, subType } = props;
    let { value = "" } = props;

    const numOfSpaces = typeof value === "string" ? value?.trim().split(" ").length - 1 || 0 : 0;
    let maxLength = props?.maxLength;
    let minLength = props?.minLength;
    if (maxLength && lengthIgnoreSpaces) maxLength = maxLength + numOfSpaces;
    if (minLength && lengthIgnoreSpaces) minLength = minLength + numOfSpaces;

    if (subType === "phoneNumber") value = value?.trim?.()?.substring?.(0, maxLength)?.trim?.() || "";

    //if (lengthIgnoreSpaces) console.log("maxLength", maxLength, "minLength", minLength, "numOfSpaces", numOfSpaces);

    const onKeyDown = e => {
        const type = props.type;

        if (
            ["e", "E", "+", "-", " ", "."].includes(e.key) &&
            (type === "number" || type === "phoneNumber" || type === "taxNumber")
        )
            e.preventDefault();

        if (props?.onKeyDown) props?.onKeyDown(e);
    };

    //remove unrecognizedProps from props
    const recognizedProps = { ...props };
    delete recognizedProps.lengthIgnoreSpaces;
    delete recognizedProps.subType;

    return multilineoptions?.isMultiLine && type === "text" ? (
        <textarea
            {...recognizedProps}
            rows={multilineoptions?.numberOfLines}
            ref={ref}
            onKeyDown={onKeyDown}
            className={`${props?.className} custominput-${id} ${
                multilineoptions?.autoExpand ? "autoResize pt-8px pb-8px" : ""
            }`}
            maxLength={subType === "phoneNumber" ? undefined : maxLength}
            minLength={minLength}
            value={value}
            onChange={e => {
                if (subType === "phoneNumber") {
                    const e2 = { ...e };
                    if (e.target.value.length > maxLength) e2.target.value = e.target.value.substring(0, maxLength);
                    if (props?.onChange) props?.onChange(e2);
                } else props?.onChange(e);
            }}
        />
    ) : (
        <input
            {...recognizedProps}
            ref={ref}
            onKeyDown={onKeyDown}
            className={`${props?.className} custominput-${id}`}
            maxLength={subType === "phoneNumber" ? undefined : maxLength}
            minLength={minLength}
            value={value}
            onChange={e => {
                if (subType === "phoneNumber") {
                    const e2 = { ...e };
                    if (e.target.value.length > maxLength) e2.target.value = e.target.value.substring(0, maxLength);
                    if (props?.onChange) props?.onChange(e2);
                } else props?.onChange(e);
            }}
        />
    );
});

const LeftContentPhoneNumber = props => {
    const {
        showDropdown,
        setShowDropdown,
        setCurrentDropdownData,
        setDropdownVisible,
        phoneNumberCountryCode,
        fieldIsInvalid,
        isDisabled
    } = props;

    const onPhoneClick = () => {
        if (isDisabled) return;
        setShowDropdown(!showDropdown);
        setCurrentDropdownData(getDropdownData("countryCodeOptions"));
        setDropdownVisible(!showDropdown);
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                height: "calc(100% + 12px)"
            }}
            onClick={onPhoneClick}>
            <div
                style={{
                    width: "30px",
                    fontSize: "13px",
                    color: fieldIsInvalid
                        ? "var(--text-icons-red-500)"
                        : isDisabled
                          ? "var(--text-icons-gray-300)"
                          : "#00BB00",
                    overflow: "hidden",
                    textAlign: "center",
                    marginLeft: "3px"
                }}>
                {phoneNumberCountryCode ? `+${phoneNumberCountryCode}` : ""}
            </div>

            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.70529 3.95541C2.86801 3.79269 3.13183 3.79269 3.29455 3.95541L5.49992 6.16079L7.70529 3.95541C7.86801 3.79269 8.13183 3.79269 8.29455 3.95541C8.45726 4.11813 8.45726 4.38195 8.29455 4.54467L5.79455 7.04467C5.63183 7.20739 5.36801 7.20739 5.20529 7.04467L2.70529 4.54467C2.54257 4.38195 2.54257 4.11813 2.70529 3.95541Z"
                    fill={!isDisabled ? "#D1D1D1" : "var(--text-icons-gray-300)"}
                />
            </svg>
        </div>
    );
};

const LeftContentTaxNumber = props => {
    const { taxNumberCountryCodeState, fieldIsInvalid, isDisabled } = props;

    return (
        <div
            style={{
                width: "27px",
                fontSize: "14px",
                color: fieldIsInvalid
                    ? "var(--text-icons-red-500)"
                    : isDisabled
                      ? "var(--text-icons-gray-300)"
                      : "#00BB00",
                overflow: "hidden",
                textAlign: "center"
            }}>
            {taxNumberCountryCodeState ? `${taxNumberCountryCodeState}` : ""}
        </div>
    );
};

const ChevronDownSVG = props => {
    const { className = "", color } = props;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            color={color}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.52864 6.02864C3.78899 5.76829 4.2111 5.76829 4.47145 6.02864L8.00004 9.55723L11.5286 6.02864C11.789 5.76829 12.2111 5.76829 12.4714 6.02864C12.7318 6.28899 12.7318 6.7111 12.4714 6.97145L8.47145 10.9714C8.2111 11.2318 7.78899 11.2318 7.52864 10.9714L3.52864 6.97145C3.26829 6.7111 3.26829 6.28899 3.52864 6.02864Z"
                fill="currentColor"
            />
        </svg>
    );
};
// #endregion

// #region EXPORTED FUNCTIONS
export const defaultPlaceholderTextFunction = (selectedItems, allItems, labelAllSelected, labelNoneSelected) => {
    if (!selectedItems?.length) return labelNoneSelected;

    const nonLockedItems = allItems?.filter(item => !item?.locked);
    const areAllSelected = selectedItems?.length === nonLockedItems?.length;
    if (areAllSelected) return `${labelAllSelected} (${nonLockedItems?.length})`;

    if (selectedItems?.length === 1) return selectedItems[0]?.name;

    return `${selectedItems?.length} selected`;
};
// #endregion

// #region HELPER FUNCTIONS
const validateTaxNumber = (taxNumber, countryCode) => {
    if (!taxNumber)
        return {
            isValid: false,
            message: "Tax number is required"
        };

    const countryCodeTMP = countryCode.toLowerCase();
    const taxNumberTMP = taxNumber.replaceAll(/\s/g, "");

    let minLength = 9;
    let maxLength = 9;

    if (countryCodeTMP === "no") {
        //For Norwegian numbers it's 9 numbers
        minLength = 9;
        maxLength = 9;
    }
    //For Danish Reg numbers it's 8 digits
    else if (countryCodeTMP === "dk") {
        minLength = 8;
        maxLength = 8;
    }

    const isValid = taxNumberTMP.length >= minLength && taxNumberTMP.length <= maxLength;
    const message = isValid
        ? ""
        : minLength === maxLength
          ? `Length must be ${minLength} characters`
          : `Length must be between ${minLength} and ${maxLength} characters`;

    return {
        isValid,
        message
    };
};

const validatePhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber)
        return {
            isValid: false,
            message: "Phone number is required"
        };

    const countryCodeTMP = countryCode.toLowerCase();
    const phoneNumberTMP = phoneNumber.replaceAll(/\s/g, "");

    let minLength = 8;
    let maxLength = 8;

    if (countryCodeTMP === "no") {
        minLength = 8;
        maxLength = 8;
    } else if (countryCodeTMP === "dk") {
        minLength = 8;
        maxLength = 8;
    }

    const isValid = phoneNumberTMP.length >= minLength && phoneNumberTMP.length <= maxLength;
    const message = isValid
        ? ""
        : minLength === maxLength
          ? `Length must be ${minLength} characters`
          : `Length must be between ${minLength} and ${maxLength} characters`;

    return {
        isValid,
        message
    };
};

const validateRegistrationNumber = (registrationNumber, countryCode) => {
    if (!registrationNumber) {
        return {
            isValid: false,
            message: "Registration number is required"
        };
    }

    const countryCodeTMP = countryCode.toLowerCase();

    if (countryCodeTMP === "no") return validateTaxNumber(registrationNumber, countryCodeTMP);
    if (countryCodeTMP === "dk") return validateTaxNumber(registrationNumber, countryCodeTMP);
    else return validateTaxNumber(registrationNumber, countryCodeTMP);
};

const getTaxCountryCodeData = countryCode => {
    const countryCodeTMP = countryCode.toLowerCase();

    //Same as tax number for now
    if (countryCodeTMP === "no") return { minLength: 9, maxLength: 9, lengthIgnoreSpaces: true };
    if (countryCodeTMP === "dk") return { minLength: 8, maxLength: 8, lengthIgnoreSpaces: true };
    else return { minLength: 9, maxLength: 9, lengthIgnoreSpaces: true };
};

const getRegistrationCountryCodeData = countryCode => {
    return getTaxCountryCodeData(countryCode); //Same as tax number for now
};
// #endregion
